import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';

/**
 * FloatingHScrollSlider
 *
 * A draggable, always-visible horizontal slider that shadow-controls a
 * `overflow-x-auto` element somewhere in the DOM (selected by `targetSelector`).
 * Useful for wide tables where the native scrollbar is below the fold while
 * the user is reading rows lower in the viewport.
 *
 *   <FloatingHScrollSlider targetSelector='[data-testid="my-table-wrap"]' />
 *
 * - Auto-hides when the target doesn't actually overflow.
 * - Position: fixed at the bottom-center of the viewport.
 * - Click anywhere on the track to jump; drag the thumb to scrub.
 * - Mouse wheel over the track scrolls the table horizontally.
 */
export const FloatingHScrollSlider = ({ targetSelector, label = 'Drag to scroll table' }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const targetRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [thumb, setThumb] = useState({ left: 0, width: 60 });

  const sync = useCallback(() => {
    const t = targetRef.current;
    if (!t) return;
    const overflow = t.scrollWidth - t.clientWidth;
    if (overflow < 8) {
      setVisible(false);
      return;
    }
    setVisible(true);
    // The track only exists once visible=true (next render). Defer position
    // calc by one frame so trackRef.current is populated.
    requestAnimationFrame(() => {
      const track = trackRef.current;
      if (!track) return;
      const trackW = track.clientWidth;
      const ratio = t.clientWidth / t.scrollWidth;
      const thumbW = Math.max(48, Math.round(trackW * ratio));
      const left = overflow > 0 ? Math.round((t.scrollLeft / overflow) * (trackW - thumbW)) : 0;
      setThumb({ left, width: thumbW });
    });
  }, []);

  useEffect(() => {
    targetRef.current = document.querySelector(targetSelector);
    const t = targetRef.current;
    if (!t) {
      // try again after a tick (the target may not have mounted yet)
      const id = setTimeout(() => {
        targetRef.current = document.querySelector(targetSelector);
        sync();
      }, 200);
      return () => clearTimeout(id);
    }
    sync();
    t.addEventListener('scroll', sync, { passive: true });
    // ResizeObserver on the wrap catches viewport-level resizes; observe
    // children too so layout-driven scrollWidth changes are caught.
    const ro = new ResizeObserver(sync);
    ro.observe(t);
    Array.from(t.children || []).forEach((c) => ro.observe(c));
    window.addEventListener('resize', sync);
    return () => {
      t.removeEventListener('scroll', sync);
      ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [targetSelector, sync]);

  // Re-poll periodically — covers tab-switches that re-mount the table and
  // any content changes that don't trigger ResizeObserver.
  useEffect(() => {
    const id = setInterval(() => {
      const next = document.querySelector(targetSelector);
      if (next !== targetRef.current) {
        const old = targetRef.current;
        if (old) old.removeEventListener('scroll', sync);
        targetRef.current = next;
        if (next) next.addEventListener('scroll', sync, { passive: true });
      }
      sync();
    }, 600);
    return () => clearInterval(id);
  }, [targetSelector, sync]);

  const onTrackClick = (e) => {
    if (draggingRef.current) return;
    const t = targetRef.current;
    const track = trackRef.current;
    if (!t || !track) return;
    const rect = track.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const max = t.scrollWidth - t.clientWidth;
    t.scrollLeft = Math.max(0, Math.min(max, ratio * max));
  };

  const startDrag = (clientX) => {
    const t = targetRef.current;
    const track = trackRef.current;
    if (!t || !track) return;
    draggingRef.current = true;
    const startScrollLeft = t.scrollLeft;
    const trackW = track.clientWidth;
    const overflow = t.scrollWidth - t.clientWidth;
    const ratio = trackW > 0 ? overflow / (trackW - thumb.width) : 0;
    const startX = clientX;

    const onMove = (ev) => {
      const x = ev.clientX !== undefined ? ev.clientX : (ev.touches?.[0]?.clientX || 0);
      const dx = x - startX;
      t.scrollLeft = Math.max(0, Math.min(overflow, startScrollLeft + dx * ratio));
    };
    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
  };

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startDrag(e.clientX);
  };
  const onThumbTouchStart = (e) => {
    e.stopPropagation();
    if (e.touches?.[0]) startDrag(e.touches[0].clientX);
  };

  const onWheel = (e) => {
    const t = targetRef.current;
    if (!t) return;
    if (Math.abs(e.deltaX) + Math.abs(e.deltaY) === 0) return;
    e.preventDefault();
    t.scrollLeft += (e.deltaY || e.deltaX);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="floating-h-slider"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 w-[min(70vw,820px)] select-none"
      onWheel={onWheel}
      style={{ touchAction: 'none' }}
    >
      <div
        ref={trackRef}
        onClick={onTrackClick}
        className="relative h-3 rounded-full cursor-pointer
          bg-white/55 dark:bg-slate-800/60 backdrop-blur-md
          shadow-[0_8px_24px_-8px_rgba(15,23,42,0.35)]
          border border-white/40 dark:border-white/10"
      >
        <div
          onMouseDown={onThumbMouseDown}
          onTouchStart={onThumbTouchStart}
          className="absolute top-0 h-full rounded-full cursor-grab active:cursor-grabbing
            bg-gradient-to-r from-amber-500 via-amber-500 to-cyan-500
            shadow-md hover:brightness-110 transition-[filter] duration-150"
          style={{ left: thumb.left, width: thumb.width }}
          data-testid="floating-h-slider-thumb"
        />
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-center mt-1 text-slate-600 dark:text-slate-300/70 pointer-events-none">
        ← {label} →
      </div>
    </div>
  );
};

export default FloatingHScrollSlider;
